"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.listIpDrugEntriesQuerySchema = exports.refundIpDrugEntrySchema = exports.createIpDrugEntrySchema = exports.IP_DRUG_BILL_NUMBER_MESSAGE = exports.IP_DRUG_BILL_NUMBER_PATTERN = exports.ipDrugEntryStatusEnum = exports.IP_DRUG_ENTRY_MAX_AMOUNT_PAISE = void 0;
const zod_1 = require("zod");
/**
 * IP DRUG ENTRIES — desk, 2026-09-25.
 *
 * A pharmacy bill raised outside this system's pharmacy module (its own bill
 * number, date and amount) entered against an ADMITTED stay, so it lands under the
 * stay's Drugs charges and in the Drugs tile of the bill footer. A refund against
 * that bill number reduces what the stay owes for it.
 *
 * AMOUNTS ARE INTEGER PAISE (ADR-043), like every money field in this API; the
 * screen converts the rupees a clerk types with `rupeesToPaise`.
 *
 * NO MONEY FIELD IS DERIVED HERE. The server captures ONE charge for an entry and
 * ONE negative charge for a refund; the totals a screen shows are the server's.
 */
/** ₹10,00,000 — a ceiling that turns a mistyped extra zero into a refusal, not a bill. */
exports.IP_DRUG_ENTRY_MAX_AMOUNT_PAISE = 100_000_000;
exports.ipDrugEntryStatusEnum = zod_1.z.enum(['ACTIVE', 'PARTIALLY_REFUNDED', 'REFUNDED']);
/** `YYYY-MM-DD` — a calendar day, not an instant. */
const dayString = zod_1.z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD.');
/**
 * A DRUG BILL NUMBER IS NUMERIC — desk, 2026-09-30: "make drug bill number should
 * numeric". Digits only: no letters, no `/`, `-` or spaces inside it.
 *
 * It is the one fact that decides whether two entries are the same pharmacy bill,
 * and free text made that unreliable — `DB-101`, `db 101` and `101` are one bill to
 * the person holding it. Applies to NEW entries only: rows entered before this rule
 * keep whatever they were typed as, and every read path still returns them as stored.
 * Shared so the form refuses a keystroke with the same rule the API refuses a request.
 */
exports.IP_DRUG_BILL_NUMBER_PATTERN = /^\d+$/;
exports.IP_DRUG_BILL_NUMBER_MESSAGE = 'The drug bill number must be numeric — digits only.';
exports.createIpDrugEntrySchema = zod_1.z.object({
    /** The pharmacy's own bill number, as printed on its bill — digits only. */
    drugBillNumber: zod_1.z
        .string()
        .trim()
        .min(1, 'The drug bill number is required.')
        .max(60)
        .regex(exports.IP_DRUG_BILL_NUMBER_PATTERN, exports.IP_DRUG_BILL_NUMBER_MESSAGE),
    billDate: dayString,
    amount: zod_1.z.number().int().positive().max(exports.IP_DRUG_ENTRY_MAX_AMOUNT_PAISE),
    remarks: zod_1.z.string().trim().max(500).optional(),
});
exports.refundIpDrugEntrySchema = zod_1.z.object({
    amount: zod_1.z.number().int().positive().max(exports.IP_DRUG_ENTRY_MAX_AMOUNT_PAISE),
    refundDate: dayString,
    /** Mandatory — a refund with no reason cannot be audited. */
    reason: zod_1.z.string().trim().min(3, 'A refund reason is required.').max(500),
});
exports.listIpDrugEntriesQuerySchema = zod_1.z.object({
    /** Matches the drug bill number, the stay's bill number, the IP number and the patient name. */
    search: zod_1.z.string().trim().max(120).optional(),
    status: exports.ipDrugEntryStatusEnum.optional(),
    /** Bill-date window, `YYYY-MM-DD`, inclusive both ends. */
    from: dayString.optional(),
    to: dayString.optional(),
    ipAdmissionId: zod_1.z.string().uuid().optional(),
    /**
     * EXACT drug bill number — the duplicate gate's lookup (desk, 2026-09-30). Not
     * `search`: that is a substring match across four fields, so asking about bill `12`
     * would answer with `120`, `312` and a patient called Twelve, and a gate that has to
     * sift a page for the one true match is a gate that misses it on a busy day.
     */
    drugBillNumber: zod_1.z.string().trim().min(1).max(60).optional(),
    page: zod_1.z.coerce.number().int().min(1).default(1),
    pageSize: zod_1.z.coerce.number().int().min(1).max(100).default(25),
});
//# sourceMappingURL=ip-drug-entry.schema.js.map